import { z } from 'zod/v3';

/**
 * A named, saved slice of the Tests list.
 *
 * `filters` is the SAME packed string the Tests list already uses everywhere
 * else — `encodeTestsListFilters` output, e.g. `status=todo&assignee=usr_1`
 * (see `web/src/lib/tests-list-filters.ts`). Storing the canonical
 * serialization rather than a second JSON shape means a view is decoded, turned
 * into route search params, and applied by the helpers that already exist, and
 * a filter added to the list later is storable with no schema change.
 */
/** RFC-235: the lens a view opens on. */
export const SavedViewLensSchema = z.enum(["tests", "controls"]);
export type SavedViewLens = z.infer<typeof SavedViewLensSchema>;

export const SavedViewDTOSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** Packed filter string; `''` is a legitimate value meaning "no filters". */
  filters: z.string(),
  /** RFC-235: where the view opens — null means the Tests list. */
  lens: SavedViewLensSchema.nullable(),
  /**
   * Shared views are visible to the whole workspace; private ones only to their
   * owner. The auditor-facing case ("look at this slice") is why views are
   * server-side rather than per-browser.
   */
  isShared: z.boolean(),
  /** True when the current user owns it — drives edit/delete affordances. */
  isOwn: z.boolean(),
  ownerName: z.string().nullable(),
  position: z.number().int(),
});

export type SavedViewDTO = z.infer<typeof SavedViewDTOSchema>;

export const CreateSavedViewInputSchema = z.object({
  name: z.string().min(1).max(60),
  description: z.string().max(280).optional(),
  filters: z.string().max(2000),
  lens: SavedViewLensSchema.optional(),
  isShared: z.boolean().optional(),
});

export type CreateSavedViewInput = z.infer<typeof CreateSavedViewInputSchema>;

export const UpdateSavedViewInputSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(60).optional(),
  // Nullable so a description can be cleared, not just changed.
  description: z.string().max(280).nullable().optional(),
  filters: z.string().max(2000).optional(),
  lens: SavedViewLensSchema.optional(),
  isShared: z.boolean().optional(),
});

export type UpdateSavedViewInput = z.infer<typeof UpdateSavedViewInputSchema>;

export const ReorderSavedViewsInputSchema = z.object({
  /** View ids in their new display order. */
  ids: z.array(z.string()).max(100),
});

export type ReorderSavedViewsInput = z.infer<
  typeof ReorderSavedViewsInputSchema
>;
